import Framework7 from "framework7";
import translate from "./i18n.ts";
import { getNextUniqueMessage, NotificationTime, onNotificationClick } from "./notifications.ts";
import { checkIfServiceWorkerAvailable } from "./serviceworker.ts";
import { TaskScheduler } from "./taskscheduler.ts";

// Define the notification options
interface NotificationOptions {
    title: string;
    body?: string;
}

const GRANTED = 'granted';
const LOG_PREFIX = 'notifications-web:';
const ICON = '/icons/icon-128.webp'; // TODO should be png for better compatibility

const scheduler = new TaskScheduler();

async function showNotification(f7: Framework7, options: NotificationOptions) {
    const permission = await requestPermissions(); // re-check permission at the time of the notification

    if (permission) { // check granted permissions
        const notifOptions = { body: options.body, icon: ICON, tag: 'scheduled' };
        
        const serviceWorkerAvail = await checkIfServiceWorkerAvailable();
        if (serviceWorkerAvail) { // use service worker if available
            const reg = await navigator.serviceWorker.getRegistration();
            if (!reg) {
                console.error(`${LOG_PREFIX} No service worker registration found`);
                return;
            }
            // Show the notification using the service worker
            reg.showNotification(options.title, notifOptions);
        } else {
            const notif = new Notification(options.title, notifOptions);
            // When a notification is clicked
            notif.onclick = function () {
                onNotificationClick(f7);
                window.focus(); // Bring the browser window to the front
            };
        }
        console.debug(`${LOG_PREFIX} showing notification ${JSON.stringify(options)} using ${serviceWorkerAvail ? 'service worker' : 'web notification'}`);
    }
}

// Function to schedule a notification
function scheduleNotification(f7: Framework7, hour: number, minute: number, options: NotificationOptions) {
    const now = new Date();
    const scheduledTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);

    // If the scheduled time is in the past, schedule it for the next day
    if (scheduledTime.getTime() < now.getTime()) {
        scheduledTime.setDate(scheduledTime.getDate() + 1); // next day
    }
    const delay = scheduledTime.getTime() - now.getTime();

    // schedule at the notification time
    const logDescription = `${LOG_PREFIX} Scheduled notification title = ${options.title}`;

    scheduler.scheduleTask(async () => {
        await showNotification(f7, options);
    }, delay, logDescription);
}

async function requestPermissions(): Promise<boolean> {
    const permission = Notification?.permission;

    if (permission === GRANTED) {
        console.debug(`${LOG_PREFIX} permission granted`);
        return true;

    } else {
        console.debug(`${LOG_PREFIX} permission not granted val="${Notification?.permission}", requesting`);
        const permission = await Notification.requestPermission();
        if (permission === GRANTED) {
            console.debug(`${LOG_PREFIX} permission granted`);
            return true;
        } else {
            console.debug(`${LOG_PREFIX} permission not granted = '${permission}'`);
            return false;
        }
    }
}


export function isWebNotificationSupported() {
    return 'Notification' in window;
}

export async function scheduleWebNotifications(notifTimes: NotificationTime[], f7: Framework7) {
    // Check if notifications are supported and request permission
    if (!isWebNotificationSupported()) {
        console.log(`${LOG_PREFIX} Web Notifications are not supported in this browser.`);
        return;
    }

    // check granted permissions
    const permission = await requestPermissions();
    if (!permission) return;

    // Cancel all previous tasks before scheduling a new one
    scheduler.cancelAllTasks();

    notifTimes.forEach(notification => {
        scheduleNotification(f7, notification.hour, notification.minute, {
            title: getNextUniqueMessage(), // different sentence every time
            body: translate("notification.subtitle")
        });
    });
}

